import React from 'react'
import styled from '@emotion/styled'
import theme from '@nextretreat/ui-components/dist/Theme'
import { rem } from 'polished'
import PropTypes from 'prop-types'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { BOXSHADOWS, radius, space } from 'Theme'
import arrowIcon from 'assets/images/svg/arrow-carousel.svg'
import { Box } from 'components/atoms/Layout'
import RouterLink from 'sharedComponents/RouterLink'

import 'swiper/css'
import 'swiper/css/pagination'

const DIRECTIONS = {
  LEFT: `transform: rotate(0deg);`,
  RIGHT: `transform: rotate(180deg);`,
}

const ArrowImg = styled('img')`
  height: ${rem(12)};
`

const SIZE = rem(32)

const Button = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${SIZE};
  height: ${SIZE};
  border-radius: ${radius.circle};
  background-color: ${theme.COLORS.WHITE};
  box-shadow: ${BOXSHADOWS.DARK};
  margin-left: ${space.s};
  opacity: 0;
  ${({ direction }) => DIRECTIONS[direction]}
  transition: opacity 0.3s;
`

const StyledBox = styled(Box)`
  position: relative;
  @media (hover: hover) {
    &:hover ${Button} {
      opacity: 0.9;
      transition: opacity 0.3s;
    }
  }
`

const ButtonWrapper = styled('div')`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 1;
  ${({ direction }) =>
    direction === 'RIGHT' &&
    `
     right: 0;
     margin-right: ${space.s};
  `}
`

const ArrowButton = ({ onClick, direction }) => (
  <ButtonWrapper direction={direction}>
    <Button type="button" onClick={onClick} direction={direction}>
      <ArrowImg src={arrowIcon} />
    </Button>
  </ButtonWrapper>
)

ArrowButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  direction: PropTypes.string,
}

const SliderWrapper = styled.div`
  .swiper-pagination-bullet {
    opacity: 0.6;
    background: ${theme.COLORS.WHITE};
  }
  .swiper-pagination-bullet-active {
    background: ${theme.COLORS.WHITE};
    opacity: 0.9;
  }
  .swiper {
    border-radius: 4px;
  }
`

const params = {
  observer: true,
  slidesPerView: 1,
  loop: true,
  modules: [Pagination],
  spaceBetween: 1,
  grabCursor: true,
  cssMode: true,
  pagination: {
    clickable: true,
    dynamicBullets: true,
  },
}

const VenueSlider = ({
  id,
  className,
  slide: Slide,
  thumbnailUrls,
  sliderMaxWidth,
  getDetailUrlByVenueId,
}) => {
  const [swiper, setSwiper] = React.useState(null)
  React.useEffect(() => {
    let timeout
    if (swiper !== null) {
      timeout = setTimeout(() => swiper.update(), 500)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [swiper]) // fixing first opening not loaded
  const handleNextClick = () => {
    if (swiper !== null) {
      swiper.slideNext()
    }
  }
  const handlePreviousClick = () => {
    if (swiper !== null) {
      swiper.slidePrev()
    }
  }
  return (
    <StyledBox className={className} maxWidth={sliderMaxWidth}>
      <RouterLink
        data-cy="venue-card"
        to={getDetailUrlByVenueId(id)}
        target="_blank"
      >
        <SliderWrapper>
          <Swiper onSwiper={setSwiper} {...params}>
            {thumbnailUrls.map((image, index) => (
              <SwiperSlide key={`${image}_${index}`}>
                <Slide
                  className="swiper-lazy"
                  loading="lazy"
                  src={image.endsWith('webp') ? image : `${image}?format=webp`}
                  alt="Swiper image"
                />
                <div className="swiper-lazy-preloader" />
              </SwiperSlide>
            ))}
          </Swiper>
        </SliderWrapper>
      </RouterLink>
      <ArrowButton onClick={() => handlePreviousClick()} direction="LEFT" />
      <ArrowButton
        onClick={() => handleNextClick()}
        isRotated
        direction="RIGHT"
      />
    </StyledBox>
  )
}

VenueSlider.propTypes = {
  className: PropTypes.string,
  id: PropTypes.number.isRequired,
  slide: PropTypes.object.isRequired,
  getDetailUrlByVenueId: PropTypes.func.isRequired,
  thumbnailUrls: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  sliderMaxWidth: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

export default VenueSlider
